import { ComponentPropsWithoutRef, FC } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Props extends ComponentPropsWithoutRef<typeof Dialog> {
	title: string;
	details?: string;
	confirmText?: string;
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * ConfirmDialog renders a dialog with two options, one for confirming an action and
 * the other one to close the dialog (or cancel the action).
 *
 * @param title       the dialog title
 * @param details     the optional dialog message
 * @param confirmText the text used to render the "confirm" button;
 *                    cancel button will always show text "cancel"
 * @param onCancel    the "cancel action" handler
 * @param onConfirm   the "confirm action" handler
 * @param props
 * @returns           a dialog with two options, i.e. "confirm" or "cancel" a certain action
 */
const ConfirmDialog: FC<Props> = ({ title, details, confirmText, onConfirm, onCancel, ...props }) => {
	const { t } = useTranslation();

	return (
		<Dialog open={props.open} onClose={onCancel}>
			<DialogTitle>{title}</DialogTitle>

			{details && (
				<DialogContent>
					<DialogContentText>{details}</DialogContentText>
				</DialogContent>
			)}

			<DialogActions>
				<Button className="confirm" onClick={onConfirm}>
					{confirmText ?? t("ok")}
				</Button>
				<Button className="cancel" onClick={onCancel}>
					{t("cancel")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConfirmDialog;
